import * as WebBrowser from 'expo-web-browser';
import { Linking, Platform } from 'react-native';
import showAlert from 'src/core/utils/showAlert';
import Colors from 'src/core/utils/Colors';
import Translations from 'src/core/utils/Translations';
import ClipboardService from 'src/data/services/ClipboardService';
import SentryService from 'src/data/services/SentryService';

const TAG = '[LinkingService] ->';

// http(s) open in the in-app browser overlay; tel:/mailto: are handed to the OS client.
const SUPPORTED_SCHEME_RE = /^(https?|mailto|tel):/i;

function isValidUrl(url: unknown): url is string {
	if (typeof url !== 'string') return false;
	return SUPPORTED_SCHEME_RE.test(url.trim());
}

function isValidEmail(email: unknown): email is string {
	if (typeof email !== 'string') return false;
	const trimmed = email.trim();
	if (!trimmed || /\.\./.test(trimmed)) return false;
	return /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/.test(trimmed);
}

function isValidPhoneNumber(phone: unknown): phone is string {
	if (typeof phone !== 'string') return false;
	const normalized = phone.trim().replace(/[\s()-]/g, '');
	return /^[+]?\d{3,}$/.test(normalized);
}

// Strip the scheme so the copy fallback offers a clean value (e.g. the address, not "mailto:…").
function copyValue(url: string): string {
	return url.trim().replace(/^(mailto:|tel:)/i, '');
}

/**
 * Hands a URL to the browser and says whether that actually happened.
 *
 * react-native-web maps openURL onto window.open, which returns null when a popup blocker or a
 * sandboxed iframe stops it -- and resolves either way. The app runs inside exactly such an iframe
 * on topicusevents.nl/app, so a blocked link would have looked like a dead button: no browser tab,
 * no message, nothing to copy. Native keeps the RN path, where openURL rejects on failure.
 */
async function handOver(url: string): Promise<boolean> {
	if (Platform.OS !== 'web' || typeof window === 'undefined') {
		await Linking.openURL(url);

		return true;
	}

	// tel: and mailto: hand off to the OS and leave no window behind, so a null there says nothing.
	if (/^(tel|mailto):/i.test(url)) {
		window.location.href = url;

		return true;
	}

	// With 'noopener' window.open always returns null, so the opener is cut after the check instead.
	const opened = window.open(url, '_blank');

	if (opened === null) {
		return false;
	}

	opened.opener = null;

	return true;
}

type LinkKind = 'phone' | 'email' | 'url';

function titleForKind(kind: LinkKind): string {
	if (kind === 'phone') return Translations.errorOpenPhoneTitle;
	if (kind === 'email') return Translations.errorOpenEmailTitle;
	return Translations.errorOpenLinkTitle;
}

// Graceful failure: report to Sentry and offer to copy the value so the user is never stuck.
function reportFailure(value: string, kind: LinkKind, internalMessage: string) {
	SentryService.captureEvent({ message: internalMessage, extra: { value } });
	// Both answers are real: copy the number, or acknowledge. That is neither a notice nor a
	// confirmation, so the button list travels as it is -- unchanged on native, and on web the
	// dialog draws both. Dismissing runs nothing, exactly as it does on Android today.
	showAlert({
		title: titleForKind(kind),
		message: Translations.errorOpenValueMessage(value),
		buttons: [
			{
				text: Translations.buttonCopy,
				onPress: () => {
					ClipboardService.copyToClipboard({ text: value }).catch(() => undefined);
				},
			},
			{ text: Translations.buttonOk },
		],
	});
}

const LinkingService = {
	/** Opens a web URL in an in-app browser overlay. tel:/mailto: are delegated to the OS client. */
	async openUrlInternally({ url }: { url: string }): Promise<void> {
		if (!isValidUrl(url)) {
			console.warn(TAG, 'Invalid URL passed to openUrlInternally:', url);
			return;
		}
		const trimmed = url.trim();
		if (trimmed.startsWith('tel:') || trimmed.startsWith('mailto:')) {
			await LinkingService.openUrlExternally({ url: trimmed });
			return;
		}
		try {
			await WebBrowser.openBrowserAsync(trimmed, {
				toolbarColor: Colors.secondary,
				secondaryToolbarColor: Colors.secondary,
				controlsColor: Colors.white,
			});
		} catch (error) {
			console.warn(TAG, 'openBrowserAsync failed, falling back externally:', error);
			await LinkingService.openUrlExternally({ url: trimmed });
		}
	},

	/** Opens a URL with the OS handler (system browser, mail app, dialer, …). */
	async openUrlExternally({ url }: { url: string }): Promise<void> {
		if (!isValidUrl(url)) {
			console.warn(TAG, 'Invalid URL passed to openUrlExternally:', url);
			return;
		}
		const trimmed = url.trim();
		try {
			// openURL rejects when no app can handle the scheme (e.g.
			if (!(await handOver(trimmed))) {
				reportFailure(copyValue(trimmed), 'url', 'URL could not be opened externally.');
			}
		} catch (error) {
			console.warn(TAG, 'Linking.openURL failed:', error);
			reportFailure(copyValue(trimmed), 'url', 'URL could not be opened externally.');
		}
	},

	/** Opens the device mail client for the given address. */
	async openEmail({ email }: { email: string }): Promise<void> {
		if (!isValidEmail(email)) {
			console.warn(TAG, 'Invalid email provided:', email);
			reportFailure(email, 'email', 'Attempted to open invalid email.');
			return;
		}
		try {
			await handOver(`mailto:${email.trim()}`);
		} catch (error) {
			console.warn(TAG, 'openEmail failed:', error);
			reportFailure(email.trim(), 'email', 'Email could not be opened.');
		}
	},

	/** Opens the device dialer for the given phone number. */
	async openPhoneNumber({ phoneNumber }: { phoneNumber: string }): Promise<void> {
		if (!isValidPhoneNumber(phoneNumber)) {
			console.warn(TAG, 'Invalid phone number provided:', phoneNumber);
			reportFailure(phoneNumber, 'phone', 'Attempted to open invalid phone number.');
			return;
		}
		// tel: URLs may not contain spaces, dashes or parentheses, iOS rejects them otherwise.
		const sanitized = phoneNumber.replace(/[\s()-]/g, '');
		const url = `tel:${sanitized}`;
		try {
			// canOpenURL catches the "no dialer" case up front (simulator, tablet), where openURL would otherwise
			// resolve silently and leave the user without the copy fallback. Not on web: there it answers true
			// for everything, so asking it would only make the check look like it happened.
			if (Platform.OS !== 'web' && !(await Linking.canOpenURL(url))) {
				reportFailure(phoneNumber.trim(), 'phone', 'No app available to open phone number.');
				return;
			}
			await handOver(url);
		} catch (error) {
			console.warn(TAG, 'openPhoneNumber failed:', error);
			reportFailure(phoneNumber.trim(), 'phone', 'Phone number could not be opened.');
		}
	},
};

export default LinkingService;
